import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import SettingsPageHeader from "../../shared/SettingsPageHeader";
import SettingsPrimaryButton from "../../shared/SettingsPrimaryButton";
import {
  SETTINGS_HORIZONTAL_PADDING,
  SETTINGS_SCROLL_BOTTOM_PADDING,
  settingsScrollFullBleedSx,
} from "../../shared/styles";
import { fetchAccessibleGroups } from "../../../../../store/features/groupsApi";
import {
  clearWidgetSettingsGroup,
  createWidgetSettingsByGroup,
  fetchWidgetSettingsAdminByGroup,
  updateWidgetSettingsByGroup,
} from "../../../../../store/features/widgetSettingsGroupApi";
import AppearanceTab from "../ChatWidget/AppearanceTab";
import BehaviourTab from "../ChatWidget/BehaviourTab";
import ChatWidgetGroupSelect from "../ChatWidget/ChatWidgetGroupSelect";
import ChatWidgetSubTabs from "../ChatWidget/ChatWidgetSubTabs";
import InstallTab from "../ChatWidget/InstallTab";
import WidgetPreviewPanel from "../ChatWidget/preview/WidgetPreviewPanel";
import { PREVIEW_NATURAL_HEIGHT } from "../ChatWidget/preview/usePreviewScale";
import {
  ChatWidgetSettingsSkeleton,
  WidgetPreviewSkeleton,
} from "../ChatWidget/ChatWidgetPageSkeleton";
import {
  DEFAULT_WIDGET_SETTINGS,
  WIDGET_SUB_TABS,
  applySaveResponseToForm,
  buildInstallSnippet,
  buildWidgetFormData,
  getActiveGroups,
  getDefaultChatWidgetGroupId,
  getPreChatFieldLabelError,
  hasAppearanceTextErrors,
  isGroupArchived,
  mapApiSettingsToForm,
  normalizeBehaviourSettings,
  normalizePreChatFields,
} from "../ChatWidget/constants";
import { getKbGroupId, setKbGroupId } from "../../../../../utils/kbGroupId";

const ACCEPTED_LOGO_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const MAX_LOGO_SIZE_MB = 5;

function revokeBlobUrl(url) {
  if (url?.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
}

function ChatWidgetPage() {
  const dispatch = useDispatch();
  const businessId = useSelector((state) => state.loginToken?.response?.user?.business_id);
  const { list: groups, listLoading } = useSelector((state) => state.peopleGroups);
  const { loading, saving, configured } = useSelector((state) => state.widgetSettingsGroup);

  const [selectedGroupId, setSelectedGroupId] = useState(() => getKbGroupId() || "");
  const [settingsReady, setSettingsReady] = useState(false);
  const [activeTab, setActiveTab] = useState("appearance");
  const [previewPlatform, setPreviewPlatform] = useState("Web");
  const [settings, setSettings] = useState(DEFAULT_WIDGET_SETTINGS);
  const [logoFile, setLogoFile] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const fileInputRef = useRef(null);

  const activeGroups = useMemo(() => getActiveGroups(groups), [groups]);

  const defaultGroupId = useMemo(
    () => getDefaultChatWidgetGroupId(groups),
    [groups],
  );

  const resolvedGroupId = selectedGroupId || defaultGroupId;

  const selectedGroup = useMemo(
    () => groups.find((group) => group.id === resolvedGroupId),
    [groups, resolvedGroupId],
  );

  const isSelectedGroupArchived = isGroupArchived(selectedGroup);

  useEffect(() => {
    dispatch(fetchAccessibleGroups());
    return () => {
      dispatch(clearWidgetSettingsGroup());
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedGroupId && isSelectedGroupArchived && defaultGroupId) {
      setSelectedGroupId(defaultGroupId);
      setKbGroupId(defaultGroupId);
    }
  }, [selectedGroupId, isSelectedGroupArchived, defaultGroupId]);

  useEffect(() => {
    if (selectedGroupId || !defaultGroupId) return;
    setSelectedGroupId(defaultGroupId);
    setKbGroupId(defaultGroupId);
  }, [selectedGroupId, defaultGroupId]);

  useEffect(() => {
    if (!groups.length || !selectedGroupId) return;
    const group = groups.find((g) => g.id === selectedGroupId);
    if (!group && defaultGroupId) {
      setSelectedGroupId(defaultGroupId);
      setKbGroupId(defaultGroupId);
    }
  }, [groups, selectedGroupId, defaultGroupId]);

  const handleGroupChange = useCallback((groupId) => {
    if (!groupId) return;
    setKbGroupId(groupId);
    setSelectedGroupId(groupId);
  }, []);

  const loadSettings = useCallback(
    async (groupId, { silent = false } = {}) => {
      if (!groupId) return;
      const result = await dispatch(
        fetchWidgetSettingsAdminByGroup({ groupId, silent }),
      );
      if (fetchWidgetSettingsAdminByGroup.fulfilled.match(result)) {
        const mapped = mapApiSettingsToForm(result.payload);
        if (mapped) {
          setSettings((prev) => {
            revokeBlobUrl(prev.logo);
            return mapped;
          });
          setLogoFile(null);
        } else {
          setSettings((prev) => {
            revokeBlobUrl(prev.logo);
            return DEFAULT_WIDGET_SETTINGS;
          });
          setLogoFile(null);
        }
      } else if (!silent) {
        setSettings((prev) => {
          revokeBlobUrl(prev.logo);
          return DEFAULT_WIDGET_SETTINGS;
        });
        setLogoFile(null);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!resolvedGroupId) {
      setSettingsReady(false);
      return;
    }

    let cancelled = false;
    setSettingsReady(false);
    setLogoFile(null);

    loadSettings(resolvedGroupId).finally(() => {
      if (!cancelled) {
        setSettingsReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [resolvedGroupId, loadSettings]);

  useEffect(
    () => () => {
      setSettings((prev) => {
        revokeBlobUrl(prev.logo);
        return prev;
      });
    },
    [],
  );

  const handleSettingsChange = useCallback((patch) => {
    setSettings((prev) => normalizeBehaviourSettings({ ...prev, ...patch }));
  }, []);

  const handleUploadLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      toast.error("Please upload a PNG, JPG, or WEBP image.");
      return;
    }
    if (file.size > MAX_LOGO_SIZE_MB * 1024 * 1024) {
      toast.error(`Logo must be smaller than ${MAX_LOGO_SIZE_MB}MB.`);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setSettings((prev) => {
      revokeBlobUrl(prev.logo);
      return { ...prev, logo: objectUrl };
    });
    setLogoFile(file);
    setLogoLoading(false);
  };

  const snippet = useMemo(
    () =>
      configured && !isSelectedGroupArchived
        ? buildInstallSnippet(businessId, resolvedGroupId)
        : "",
    [businessId, resolvedGroupId, configured, isSelectedGroupArchived],
  );

  const applySavedSettings = useCallback(
    async (payload, hadLogoUpload) => {
      setSettings((prev) => {
        revokeBlobUrl(prev.logo);
        return applySaveResponseToForm(prev, payload);
      });
      setLogoFile(null);

      if (hadLogoUpload && !payload?.data?.logo && resolvedGroupId) {
        await loadSettings(resolvedGroupId, { silent: true });
      }
    },
    [loadSettings, resolvedGroupId],
  );

  const handleSave = async () => {
    if (!resolvedGroupId) {
      toast.error("Please select a group.");
      return;
    }
    if (isSelectedGroupArchived) {
      toast.error("Widget settings cannot be changed for an archived group.");
      return;
    }

    if (hasAppearanceTextErrors(settings)) {
      toast.error("Greeting text, subtitle, and welcome message are required.");
      setActiveTab("appearance");
      return;
    }

    if (settings.preChatForm) {
      const invalidLabel = normalizePreChatFields(settings.preChatFields).find(
        (field) => !field.locked && getPreChatFieldLabelError(field.label),
      );
      if (invalidLabel) {
        toast.error("Pre-chat field labels cannot be empty or \".\".");
        setActiveTab("behaviour");
        return;
      }
    }

    const hadLogoUpload = Boolean(logoFile);
    const body = buildWidgetFormData(settings, logoFile);
    const saveThunk = configured ? updateWidgetSettingsByGroup : createWidgetSettingsByGroup;
    const result = await dispatch(
      saveThunk({ groupId: resolvedGroupId, body }),
    );

    if (saveThunk.fulfilled.match(result)) {
      if (result.payload?.success) {
        toast.success("Widget settings saved.");
        await applySavedSettings(result.payload, hadLogoUpload);
      } else {
        toast.error(result.payload?.message || "Failed to save widget settings.");
      }
    } else {
      toast.error("Failed to save widget settings.");
    }
  };

  const renderTabContent = () => {
    if (activeTab === "appearance") {
      return (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_LOGO_TYPES.join(",")}
            onChange={handleLogoChange}
            style={{ display: "none" }}
          />
          <AppearanceTab
            settings={settings}
            onChange={handleSettingsChange}
            logoPreview={settings.logo}
            onUploadLogoClick={handleUploadLogoClick}
            logoLoading={logoLoading}
            hasPendingLogo={Boolean(logoFile)}
            disabled={isSelectedGroupArchived}
          />
        </>
      );
    }
    if (activeTab === "behaviour") {
      return <BehaviourTab settings={settings} onChange={handleSettingsChange} />;
    }
    return (
      <InstallTab
        snippet={snippet}
        configured={configured && !isSelectedGroupArchived}
        groupArchived={isSelectedGroupArchived}
      />
    );
  };

  const isLoading = listLoading || loading || !resolvedGroupId || !settingsReady;

  return (
    <Box
      sx={{
        ...settingsScrollFullBleedSx,
        display: "flex",
        alignItems: "flex-start",
        flexDirection: { xs: "column", lg: "row" },
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          width: "100%",
          pl: SETTINGS_HORIZONTAL_PADDING,
          pr: { xs: SETTINGS_HORIZONTAL_PADDING, lg: "40px" },
          pb: SETTINGS_SCROLL_BOTTOM_PADDING,
          borderRight: { xs: "none", lg: "1px solid #E5E9EC" },
        }}
      >
        <Box sx={{ width: "100%" }}>
          <SettingsPageHeader
            title="Chat Widget Settings"
            description="Customize your chat widget appearance and behavior"
          />

          <ChatWidgetGroupSelect
            groups={activeGroups}
            value={resolvedGroupId}
            onChange={handleGroupChange}
            loading={listLoading}
          />

          <ChatWidgetSubTabs
            tabs={WIDGET_SUB_TABS}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          {isLoading ? (
            <ChatWidgetSettingsSkeleton activeTab={activeTab} />
          ) : (
            <>
              <Box sx={{ mt: "24px" }}>{renderTabContent()}</Box>
              <Box sx={{ mt: "32px" }}>
                <SettingsPrimaryButton
                  onClick={handleSave}
                  disabled={saving || !resolvedGroupId || isSelectedGroupArchived}
                  sx={{ width: "184px" }}
                >
                  {saving ? "Saving..." : "Save Widget Settings"}
                </SettingsPrimaryButton>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          width: { xs: "100%", lg: 440 },
          pl: { xs: SETTINGS_HORIZONTAL_PADDING, lg: "32px" },
          mr: '32px',
          pt: { xs: "32px", lg: 0 },
          pb: SETTINGS_SCROLL_BOTTOM_PADDING,
          display: "flex",
          flexDirection: "column",
          alignSelf: { lg: "flex-start" },
          position: { lg: "sticky" },
          top: { lg: 0 },
          minHeight: { lg: PREVIEW_NATURAL_HEIGHT + 120 },
        }}
      >
        {isLoading ? (
          <WidgetPreviewSkeleton />
        ) : (
          <WidgetPreviewPanel
            activeTab={activeTab}
            settings={settings}
            platform={previewPlatform}
            onPlatformChange={setPreviewPlatform}
            sx={{ flex: 1, minHeight: 0 }}
          />
        )}
      </Box>
    </Box>
  );
}

export default ChatWidgetPage;
